<script lang="ts">
    const component = {
        name: 'Partial_ContactListNodeSettings',
    };
    export default {
        name: component.name,
    };
</script>

<script lang="ts" setup>
    import { ref, computed, onMounted } from 'vue';
    import { JourneyNodeData } from '@journeys/Journeys.viewmodel';
    import vm from '@journeys/Journeys.viewmodel';
    import Modal from '@shared/components/Modal.vue';
    import SelectItemFromList from '@shared/components/SelectItemFromList.vue';
    import { ItemTypes } from '@shared/components/SelectItemFromList';
    import { getList } from '@cwd/Views/Lists/Lists.access';
    import { type List } from '@cwd/Views/Lists/ListsTypes.d';
    import { formatDateTime } from '@cwd/Shared/utils/formatDateTime';
    import { formatNumericText } from '@cwd/Shared/utils/formatNumericText';
    import TagsList from '@shared/components/Tags/TagsList.vue';
    import Loading from '@shared/components/Loading.vue';
    import BsAlert from '@shared/components/BsAlert.vue';
    import { AlertTypes } from '@shared/components/bsAlerts';

    const { account } = vm;

    const props = defineProps<{
        data: JourneyNodeData;
        id: number;
    }>();

    const showAddModal = ref(false);
    const showRemoveModal = ref(false);
    const selectedAddList = ref<List | null>(null);
    const selectedRemoveList = ref<List | null>(null);
    const loading = ref(false);

    const getLists = computed(() => account.listings.get('/lists'));

    onMounted(async () => {
        loading.value = true;

        if (props.data.ListIdToAdd) {
            selectedAddList.value = await getList(props.data.ListIdToAdd);
        }
        if (props.data.ListIdToRemove) {
            selectedRemoveList.value = await getList(props.data.ListIdToRemove);
        }

        loading.value = false;
    });

    const selectAddList = async (newId: string) => {
        showAddModal.value = false;
        props.data.ListIdToAdd = newId;
        selectedAddList.value = await getList(newId);
    };

    const selectRemoveList = async (newId: string) => {
        showRemoveModal.value = false;
        props.data.ListIdToRemove = newId;
        selectedRemoveList.value = await getList(newId);
    };

    const closeModal = () => {
        showAddModal.value = false;
        showRemoveModal.value = false;
    };
</script>

<template>
    <template v-if="!loading">
        <h3 class="h4">{{ $Resources.JourneyContactListNodeAddListsLabel }}</h3>

        <div class="fieldset" v-if="selectedAddList">
            <span class="label-look">
                {{ $Resources.SelectedListLabel }}
            </span>

            <h4 class="m-0-t item-heading">
                <a :href="`/Lists/${selectedAddList.ListId}`" target="_blank" rel="noopener">
                    {{ selectedAddList.Name || $Resources.NoTitleLabel }}
                    <small class="glyphicon glyphicon-new-window" aria-hidden="true"></small>
                </a>
            </h4>

            <div class="ug-values">
                <div class="ug-value">
                    <span class="ug-value-label">{{ $Resources.ListContactCountLabel }}</span>
                    <span>{{ formatNumericText(selectedAddList.ContactCount, { precision: 1, largeNumbers: true }) }}</span>
                </div>
            </div>

            <p v-if="selectedAddList.Description">{{ selectedAddList.Description }}</p>

            <TagsList v-if="selectedAddList.Tags && selectedAddList.Tags.length" :tags="selectedAddList.Tags" :readonly="true" />

            <p class="meta">
                <i class="fa fa-pencil" aria-hidden="true"></i>
                {{ $Resources.LastModifiedLabel }}
                <time :datetime="selectedAddList.LastModified">{{ formatDateTime(selectedAddList.LastModified) }}</time>
                {{ $Resources.GenericByLabel }}
                {{ selectedAddList.ModifiedBy.Name }}
            </p>
        </div>

        <BsAlert
            v-else
            :type="AlertTypes.Info"
            :content="$Resources.NoListSelectedLabel"
            :dismissible="false"
            :buttonText="$Resources.SelectListLabel"
            @button-click="showAddModal = true" />

        <div v-if="selectedAddList" class="form-group d-flex flex-wrap gap-sm">
            <button type="button" class="btn btn-default" @click="showAddModal = true">
                {{ $Resources.SelectListLabel }}
            </button>

            <button v-show="selectedAddList" type="button" class="btn btn-default" @click="selectedAddList = null">
                {{ $Resources.DeselectListLabel }}
            </button>
        </div>

        <h3 class="h4">{{ $Resources.JourneyContactListNodeRemoveListsLabel }}</h3>

        <div class="fieldset" v-if="selectedRemoveList">
            <span class="label-look">
                {{ $Resources.SelectedListLabel }}
            </span>

            <h4 class="m-0-t item-heading">
                <a :href="`/Lists/${selectedRemoveList.ListId}`" target="_blank" rel="noopener">
                    {{ selectedRemoveList.Name || $Resources.NoTitleLabel }}
                    <small class="glyphicon glyphicon-new-window" aria-hidden="true"></small>
                </a>
            </h4>

            <div class="ug-values">
                <div class="ug-value">
                    <span class="ug-value-label">{{ $Resources.ListContactCountLabel }}</span>
                    <span>{{ formatNumericText(selectedRemoveList.ContactCount, { precision: 1, largeNumbers: true }) }}</span>
                </div>
            </div>

            <p v-if="selectedRemoveList.Description">{{ selectedRemoveList.Description }}</p>

            <TagsList v-if="selectedRemoveList.Tags && selectedRemoveList.Tags.length" :tags="selectedRemoveList.Tags" :readonly="true" />

            <p class="meta">
                <i class="fa fa-pencil" aria-hidden="true"></i>
                {{ $Resources.LastModifiedLabel }}
                <time :datetime="selectedRemoveList.LastModified">{{ formatDateTime(selectedRemoveList.LastModified) }}</time>
                {{ $Resources.GenericByLabel }}
                {{ selectedRemoveList.ModifiedBy.Name }}
            </p>
        </div>

        <BsAlert
            v-else
            :type="AlertTypes.Info"
            :content="$Resources.NoListSelectedLabel"
            :dismissible="false"
            :buttonText="$Resources.SelectListLabel"
            @button-click="showRemoveModal = true" />

        <div v-if="selectedRemoveList" class="form-group d-flex flex-wrap gap-sm">
            <button type="button" class="btn btn-default" @click="showRemoveModal = true">
                {{ $Resources.SelectListLabel }}
            </button>

            <button v-show="selectedRemoveList" type="button" class="btn btn-default" @click="selectedRemoveList = null">
                {{ $Resources.DeselectListLabel }}
            </button>
        </div>
    </template>

    <Loading :show="loading" />

    <Modal :title="$Resources.ChooseHeading" :show="showAddModal || showRemoveModal" @close="closeModal" size="lg">
        <template #content>
            <div v-show="showAddModal">
                <SelectItemFromList v-if="getLists" :items="getLists" :type="ItemTypes.Lists" @update:selectedId="(newId: string) => selectAddList(newId)" />
            </div>

            <div v-show="showRemoveModal">
                <SelectItemFromList v-if="getLists" :items="getLists" :type="ItemTypes.Lists" @update:selectedId="(newId: string) => selectRemoveList(newId)" />
            </div>
        </template>
    </Modal>
</template>
